<template>
  <div
    class="relative h-full rounded transition-[width,margin] duration-1000"
    :class="isCollapsed ? 'w-[0px] mr-0' : 'w-[250px] mr-4'"
  >
    <div
      class="absolute top-3 left-0 z-10 w-[250px] transition-transform duration-1000"
      :class="isCollapsed ? '-translate-x-[205px]' : 'translate-x-0'"
    >
      <div class="flex justify-end transition-[margin] duration-1000" :class="isCollapsed ? 'mr-6' : 'mr-2'">
        <IconRoundButton
          v-capture-button-name="'toggle-sidebar'"
          class="h-9 w-9"
          :icon="IconName.Sidebar"
          :weight="IconRoundButtonWeight.Small"
          whitebg
          size="lg"
          :disabled="false"
          @click="toggleSidebar"
        />
      </div>
    </div>

    <div class="absolute inset-y-0 left-0 w-full rounded">
      <nav
        class="absolute flex h-full w-[250px] flex-col rounded border border-app-grey-light bg-white transition-transform duration-1000"
        :class="[isCollapsed ? '-translate-x-[280px] pointer-events-none' : 'translate-x-0']"
      >
        <div class="flex h-full min-h-0 flex-col">
          <div class="flex-1 min-h-0 overflow-y-auto px-3 py-6">
            <div class="flex flex-col gap-3">
              <div v-if="hasLogo" class="flex items-center justify-center px-2">
                <img class="max-w-[155px] max-h-[62px]" :src="resolvedLogo.src" />
              </div>

              <SideNavDivider v-if="hasLogo" />

              <div v-if="hasProfileSlot" class="flex flex-col gap-3">
                <slot name="profile" />
                <SideNavDivider />
              </div>

              <slot name="sections" />

              <slot />
            </div>
          </div>

          <template v-if="hasFooterSlot">
            <div
              class="z-10 flex h-[74px] shrink-0 items-center rounded-b border-t border-[#E8DFEA] bg-white p-3 shadow-sm"
            >
              <slot name="footer" />
            </div>
          </template>
        </div>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconRoundButton, IconRoundButtonWeight } from '@kiplin/web-common/design-system';
import { IconName } from '@kiplin/web-common/icon';
import { Comment, computed, ref, useSlots } from 'vue';
import SideNavDivider from '@design-system/side-nav/application/component/SideNavDivider/SideNavDivider.vue';
import type { SideNavLogo, SideNavProps } from '@design-system/side-nav/application/component/SideNav/SideNav.types';

const props = withDefaults(defineProps<SideNavProps>(), {
  openByDefault: true,
});
const isCollapsed = ref(!props.openByDefault);
const emit = defineEmits<{ toggleSidebar: [boolean] }>();
const slots = useSlots();

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
  emit('toggleSidebar', isCollapsed.value);
};

const defaultLogo: SideNavLogo = {
  src: '/img/logo-kiplin-black-large.png',
  alt: 'Kiplin',
  height: 60,
};

const resolvedLogo = computed(() => props.logo ?? defaultLogo);
const hasLogo = computed(() => Boolean(resolvedLogo.value));
const hasProfileSlot = computed(() => Boolean(slots.profile));
const hasFooterSlot = computed(() => slots.footer?.().some((node) => node.type !== Comment) === true);
</script>
